import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FilterExposedHeader from '../FilterExposedHeader';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import useAnimations from '../../../hooks/Animations/useAnimations';
import { FilterFrame, FilterSelection, GroupWrapper } from '../Filters.style';
import CheckboxSelector from '../../CheckboxComponent';
import {
  AllMakesWrapper,
  MakeCheckbox,
  MakesCount,
  OtherMakeGroupWrapper,
  ShowAllButton,
} from './OtherMakes.style';

interface OtherMakesProps {
  filterType: string;
  otherMakesOptions: any;
  confirmedFilters: FilterTypes;
  openedFiltersList: ExposedFiltersTypes;
  handleAddFilter: (filterType: string, value: string) => void;
  handleExposeFilter: (arg: string) => void;
}

const OtherMakes: React.FC<OtherMakesProps> = ({
  filterType,
  otherMakesOptions,
  confirmedFilters,
  handleAddFilter,
  handleExposeFilter,
  openedFiltersList,
}) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const listOfOptions = Object.keys(otherMakesOptions);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [showAll]);

  const selectedOtherMakesLabels = useMemo(() => {
    const seletedOtherMakesList = confirmedFilters.otherMakes;
    return seletedOtherMakesList.map((make) => otherMakesOptions[make]?.label);
  }, [otherMakesOptions, confirmedFilters.otherMakes]);

  const showFilter = openedFiltersList[filterType];

  const selectedOtherMakesFilters = useMemo(() => {
    return confirmedFilters?.otherMakes;
  }, [confirmedFilters?.otherMakes]);

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(showFilter);

  const visibleCheckboxes = useMemo(() => {
    const otherMakesLength = listOfOptions.length;
    const sortedListOfOptions = listOfOptions
      .filter((code) => code !== 'AA')
      .sort((a, b) => {
        const labelA = otherMakesOptions[a]?.label || '';
        const labelB = otherMakesOptions[b]?.label || '';
        return labelA.localeCompare(labelB);
      });

    if (listOfOptions.includes('AA')) {
      sortedListOfOptions.unshift('AA');
    }
    if (otherMakesLength > 5 && !showAll) return sortedListOfOptions.slice(0, 5);
    return sortedListOfOptions;
  }, [listOfOptions, otherMakesOptions, showAll]);

  return (
    <>
      <FilterExposedHeader
        filterType={filterType}
        selectedFilterLabels={selectedOtherMakesLabels}
        handleExposeFilter={handleExposeFilter}
        openedFiltersList={openedFiltersList}
      />
      {dropdownAnimation(
        (props, item) =>
          item && (
            <FilterSelection style={props} data-testid="other-makes-filter-group">
              <FilterFrame style={filtersAccordianAnimation}>
                <AllMakesWrapper expanded={showAll} contentHeight={`${contentHeight}px` || '0'}>
                  <OtherMakeGroupWrapper ref={contentRef}>
                    {visibleCheckboxes.map((make, index) => {
                      const { label, value, count } = otherMakesOptions[make];
                      return (
                        <MakeCheckbox key={label}>
                          <CheckboxSelector
                            key={value}
                            value={value}
                            filterType={filterType}
                            onChange={(newValue) => handleAddFilter(filterType, newValue)}
                            inputName={`${value}-${index}`}
                            checked={selectedOtherMakesFilters.includes(value)}
                            text={label}
                          />
                          <MakesCount>({count})</MakesCount>
                        </MakeCheckbox>
                      );
                    })}
                    <ShowAllButton onClick={() => setShowAll(!showAll)}>
                      {showAll
                        ? t(`vlp.filters.filterOptions.otherMakes.showFewer`)
                        : t(`vlp.filters.filterOptions.otherMakes.showAll`)}
                    </ShowAllButton>
                  </OtherMakeGroupWrapper>
                </AllMakesWrapper>
              </FilterFrame>
            </FilterSelection>
          ),
      )}
    </>
  );
};

export default OtherMakes;
